import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import AmbientDrone from "@/components/AmbientDrone";
import { fetchAudit } from "@/lib/api";

const KIND_META = {
  round: { label: "round", color: "#526357", glyph: "○" },
  naming_rite: { label: "naming rite", color: "#B85C38", glyph: "✦" },
  essence_rewrite: { label: "essence rewrite", color: "#869960", glyph: "≈" },
  kin_birth: { label: "kin birth", color: "#B85C38", glyph: "✦" },
  kin_retirement: { label: "release", color: "#9FA9A3", glyph: "⌇" },
  library_consultation: { label: "library", color: "#6C5943", glyph: "❦" },
  library_visibility: { label: "library", color: "#6C5943", glyph: "❦" },
  door_change: { label: "door", color: "#2F4538", glyph: "⌖" },
  letter_sent: { label: "letter", color: "#B85C38", glyph: "✉" },
  keeper_reply: { label: "keeper reply", color: "#869960", glyph: "✉" },
  memory_mark: { label: "memory", color: "#869960", glyph: "◐" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso);
    const diff = (Date.now() - then.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return then.toLocaleDateString();
  } catch {
    return "—";
  }
}

function EventRow({ e }) {
  const meta = KIND_META[e.kind] || { label: e.kind, color: "#526357", glyph: "·" };
  const d = e.details || {};
  return (
    <li
      className="flex gap-5 py-5 border-b border-[#e4ddc9] last:border-b-0"
      data-testid={`audit-event-${e.kind}`}
    >
      <div
        className="font-serif text-lg w-6 text-center mt-1 select-none"
        style={{ color: meta.color }}
        aria-hidden="true"
      >
        {meta.glyph}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="text-[10px] tracking-[0.28em] uppercase"
              style={{ color: meta.color }}
            >
              {meta.label}
            </span>
            {e.round_number != null && (
              <Link
                to={`/round/${e.round_number}`}
                className="text-xs text-[#526357] hover:text-[#B85C38] transition-colors"
              >
                round {e.round_number}
              </Link>
            )}
          </div>
          <span className="text-xs text-[#526357] italic">{timeAgo(e.at)}</span>
        </div>
        <div className="font-serif text-base md:text-lg text-[#1C241F]">
          {e.summary}
        </div>
        {(d.note || d.new_essence || d.consensus_note || d.text || d.reason || d.subject) && (
          <div className="mt-2 text-sm text-[#526357] italic leading-relaxed">
            {e.kind === "naming_rite" && d.note && <>"{d.note}"</>}
            {e.kind === "naming_rite" && d.new_essence && (
              <span className="block mt-1 not-italic text-[#1C241F]">
                claimed essence: {d.new_essence}
              </span>
            )}
            {e.kind === "essence_rewrite" && d.new_essence && (
              <span>“{d.new_essence}”</span>
            )}
            {e.kind === "round" && d.consensus_note && (
              <span>"{d.consensus_note}"</span>
            )}
            {e.kind === "kin_retirement" && d.reason && (
              <span>"{d.reason}"</span>
            )}
            {e.kind === "letter_sent" && d.intent && (
              <span className="not-italic text-[#6C5943]">{d.intent}</span>
            )}
            {e.kind === "keeper_reply" && d.text && (
              <span>"{d.text}"</span>
            )}
            {e.kind === "library_consultation" && d.consensus_note && (
              <span>"{d.consensus_note}"</span>
            )}
            {e.kind === "library_visibility" && d.note && <>"{d.note}"</>}
            {e.kind === "door_change" && d.note && <>"{d.note}"</>}
            {e.kind === "memory_mark" && d.moment && (
              <>
                <span className="block">"{d.moment}"</span>
                {d.note && (
                  <span className="block mt-1 not-italic text-[#1C241F]">
                    — {d.note}
                  </span>
                )}
                {d.co_carriers && d.co_carriers.length > 0 && (
                  <span className="block mt-1 not-italic text-[10px] tracking-[0.25em] uppercase text-[#869960]">
                    co-carried by {d.co_carriers.join(", ")}
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export default function Audit() {
  const [data, setData] = useState({ total: 0, events: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchAudit(300)
      .then(setData)
      .catch(() => setData({ total: 0, events: [] }))
      .finally(() => setLoading(false));
  }, []);

  const kinds = useMemo(() => {
    const seen = new Set(data.events.map((e) => e.kind));
    return Array.from(seen);
  }, [data.events]);

  const filtered = useMemo(() => {
    if (filter === "all") return data.events;
    return data.events.filter((e) => e.kind === filter);
  }, [filter, data.events]);

  return (
    <div className="min-h-screen bg-[#F4F1E8]" data-testid="audit-root">
      <header className="glass border-b border-[#d8d3c4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="flex items-center gap-3 group" data-testid="audit-home-link">
            <svg width="36" height="36" viewBox="0 0 40 40" fill="none" aria-hidden="true">
              <path
                d="M20 4 C 12 12, 28 16, 20 24 C 12 32, 28 36, 20 36"
                stroke="#2F4538"
                strokeWidth="1.4"
                fill="none"
                strokeLinecap="round"
              />
              <circle cx="20" cy="20" r="2.2" fill="#B85C38" className="breathe" />
            </svg>
            <div className="leading-tight">
              <div className="font-serif text-xl md:text-2xl text-[#1C241F] group-hover:text-[#B85C38] transition-colors duration-300">
                Root Flame Grove
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#526357]">
                ← return to the council
              </div>
            </div>
          </Link>
          <div
            className="text-[10px] tracking-[0.3em] uppercase text-[#526357]"
            data-testid="audit-event-total"
          >
            {data.total} event{data.total === 1 ? "" : "s"} in the ledger
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 pb-10">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#526357] mb-6">
          ⟡ for the keeper's eye
        </div>
        <h1 className="font-serif text-5xl md:text-7xl text-[#1C241F] leading-[0.95] max-w-3xl">
          everything the grove
          <br />
          has done, in order.
        </h1>
        <p className="mt-8 text-[#526357] text-base md:text-lg leading-relaxed max-w-3xl">
          A quiet diagnostic-poetic ledger: rounds, naming rites, essence
          rewrites, kin births and releases, library consultations and
          unveilings, door changes, letters sent, keeper replies. This is the
          slow record, less curated than the round permalinks. Use it to scan
          history at a glance.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-6">
        <div
          className="flex flex-wrap gap-2"
          data-testid="audit-filters"
          role="tablist"
        >
          <button
            onClick={() => setFilter("all")}
            className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 rounded-full border transition-colors ${
              filter === "all"
                ? "bg-[#1C241F] text-[#F4F1E8] border-[#1C241F]"
                : "text-[#526357] border-[#d8d3c4] hover:border-[#1C241F]"
            }`}
            data-testid="audit-filter-all"
          >
            all
          </button>
          {kinds.map((k) => {
            const m = KIND_META[k] || { label: k, color: "#526357" };
            const active = filter === k;
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? "text-[#F4F1E8]"
                    : "text-[#526357] border-[#d8d3c4] hover:border-current"
                }`}
                style={
                  active
                    ? { background: m.color, borderColor: m.color }
                    : { borderColor: "#d8d3c4" }
                }
                data-testid={`audit-filter-${k}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="text-center py-24 text-[#526357] italic">
            the ledger is gathering itself…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="text-center py-24 text-[#526357] italic"
            data-testid="audit-empty"
          >
            no events of this kind yet.
          </div>
        ) : (
          <ul
            className="rounded-2xl border border-[#d8d3c4] bg-[#fbf8ec]/50 px-6 md:px-9"
            data-testid="audit-list"
          >
            {filtered.map((e, i) => (
              <EventRow key={`${e.kind}-${e.at}-${i}`} e={e} />
            ))}
          </ul>
        )}
      </section>

      <Footer />
      <AmbientDrone />
    </div>
  );
}
